import { useEffect, useRef, useState } from "react";
import { Badge, Card, CardContent, CardHeader, CardTitle, Button } from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError } from "@/components/ui/Toast";
import { useProofTasks, usePatchProofTask } from "@/api/hooks/useProof";
import type { ProofTask } from "@/api/types/proof";
import type { EventStatus } from "@/api/types/event";
import { formatShortDate, isTaskOverdue, parseIsoDate } from "./eventDate";

interface ProofTasksCardProps {
  eventId: string;
  /**
   * The parent event's current status. The API instantiates proof-cadence
   * tasks as a *side effect* of the event's status mutation (POST
   * /events/:id/status), which is a completely separate query (useEvents.ts
   * only invalidates eventKeys). Without this, the card would keep showing
   * a stale empty list after the "Mark as Delivered" click until the
   * staleTime window happened to lapse — so this component watches the
   * status prop itself and force-refetches the instant it flips to
   * "delivered", rather than relying on any cross-hook cache invalidation.
   */
  eventStatus: EventStatus;
}

/**
 * Ascending by due date (nulls last), tie-broken by template_code — mirrors
 * eventDate.ts's sortTasksByDueDate, but ProofTask has no sort_offset field
 * (it isn't a templated-with-ordering resource the way T-minus tasks are),
 * so template_code is the stable secondary key instead.
 */
function sortProofTasks(tasks: ProofTask[]): ProofTask[] {
  return [...tasks].sort((a, b) => {
    if (a.due_date && b.due_date) {
      const diff = parseIsoDate(a.due_date).getTime() - parseIsoDate(b.due_date).getTime();
      if (diff !== 0) return diff;
    } else if (a.due_date && !b.due_date) {
      return -1;
    } else if (!a.due_date && b.due_date) {
      return 1;
    }
    return a.template_code.localeCompare(b.template_code);
  });
}

/**
 * Phase 6's post-event proof cadence: 24h thank-you/review-ask, 48h clip
 * delivery, 7d planner-conditional thank-you, 30d referral check-in. These
 * are only instantiated by the API once the event is marked `delivered` —
 * an empty list here is the normal pre-delivery state, not an error, so the
 * empty message says as much rather than reading like a broken feature.
 *
 * Deliberately visually distinct from TaskListCard's T-minus tasks (amber
 * left border + "After the event" label) so the two cadences never get
 * confused on a long event-detail page — they're driven by opposite ends
 * of the event's lifecycle.
 */
export function ProofTasksCard({ eventId, eventStatus }: ProofTasksCardProps) {
  const { data: tasks, refetch } = useProofTasks(eventId);
  const sorted = sortProofTasks(tasks ?? []);

  const prevStatus = useRef(eventStatus);
  useEffect(() => {
    if (prevStatus.current !== eventStatus && eventStatus === "delivered") {
      void refetch();
    }
    prevStatus.current = eventStatus;
  }, [eventStatus, refetch]);

  return (
    <Card className="border-l-4 border-l-copper" data-testid="proof-tasks-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>After the event</CardTitle>
          <p className="text-xs text-text-muted">The post-delivery proof cadence — review ask, clips, thank-yous, referral check-in.</p>
        </div>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="proof-tasks-empty">
            No proof-cadence tasks yet — these appear once the event is marked Delivered.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="proof-task-list">
            {sorted.map((task) => (
              <ProofTaskRow key={task.id} eventId={eventId} task={task} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ProofTaskRow({ eventId, task }: { eventId: string; task: ProofTask }) {
  const patchTask = usePatchProofTask();
  const [notes, setNotes] = useState(task.notes ?? "");
  const overdue = isTaskOverdue(task);

  async function setStatus(status: ProofTask["status"]) {
    try {
      await patchTask.mutateAsync({ eventId, taskId: task.id, status });
    } catch (err) {
      toastError("Couldn't update task", getErrorMessage(err));
    }
  }

  async function saveNotes() {
    if (notes === (task.notes ?? "")) return;
    try {
      await patchTask.mutateAsync({ eventId, taskId: task.id, notes });
    } catch (err) {
      toastError("Couldn't save notes", getErrorMessage(err));
    }
  }

  return (
    <li
      className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
      data-proof-task-id={task.id}
    >
      <div className="flex items-center gap-3">
        <span
          className={overdue ? "text-sm font-semibold text-danger" : "text-sm text-text-secondary"}
          data-testid={`proof-task-due-${task.id}`}
        >
          {formatShortDate(task.due_date)}
          {overdue ? " (overdue)" : ""}
        </span>
        <span
          className={
            task.status === "done"
              ? "text-sm text-text-muted line-through"
              : "text-sm text-text-primary"
          }
        >
          {task.title}
        </span>
        {task.status !== "open" && (
          <Badge variant={task.status === "done" ? "success" : "secondary"} size="sm">
            {task.status === "done" ? "Done" : "Waived"}
          </Badge>
        )}
      </div>

      <div className="flex flex-1 items-center gap-2 sm:justify-end">
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => void saveNotes()}
          placeholder="Notes"
          className="h-8 w-40 rounded-md border border-border bg-bg-card px-2 text-xs"
          aria-label={`Notes for ${task.title}`}
        />
        {task.status === "open" ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => void setStatus("done")}>
              Done
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void setStatus("waived")}>
              Waive
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => void setStatus("open")}>
            Reopen
          </Button>
        )}
      </div>
    </li>
  );
}
